import React, {Component} from "react";

import {CollapseBox} from "../../component/Box";
import {ExternalLink} from "../../component/IconLink";
import Button from "../../component/Button";
import Link from "../../component/Link";
import Code from "../../component/Code";
import ImgGallery from "../../component/ImgGallery";
import Tag, {SpecificTag} from "../../views/Tag";
import TagList from "../../views/TagList";

function getProjects() {
    return require("../../data/getProjects.js").default;
}

export default function PageContent() {
    return (<>
        <section>
            <h2>Project Portfolio</h2>
            <p>
                Ever since I started coding, I have always had small projects I would work on in my free time.
                Many were just for fun or to try out a language, library or technology.
                Over the years, I also did a fair amount of programming for internships, uni and part-time jobs.
                Many of these projects were just lying around on one of my hard drives or used outdated technologies.
                I thought that was a shame, because they took a lot of effort and love to create and I take some pride in them.
                Many of them are not suited for GitHub, so I needed another way to document my projects.
                This portfolio website is an attempt to highlight some of my past coding adventures while presenting and explaining what I learned on the way.
            </p>
            <p>
                In this article, I will explain some of the notable technologies that I used to create this web app, showing quite a bit of code to explain the concepts and to highlight pieces of my code.
            </p>
        </section>
        <section>
            <h2>React</h2>
            <p>
                The <ExternalLink href="https://react.dev/">React JavaScript library</ExternalLink> allows creating web-based apps based on <b>Components</b>.
                Components can be classes or functions that render a specific piece of HTML, potentially using model data.
                React Components have a lifecycle that automatically re-renders Components whose state has changed and it allows you to initialize data or to attach and detach any handlers.
            </p>
            <p>
                The final HTML is not written in HTML itself, but a JavaScript syntax extension called <b>JSX that looks like HTML</b>.
                Components can then be smuggled into the JSX to create a hierarchical interface structure and properties to specify Component settings can be passed as pseudo-HTML attributes.
            </p>
            <p>
                This first code demo is a <code>Tag</code> Component that I use to categorize all my projects.
                It takes a dictionary (<code>this.props.model</code>) for details about the tag and then renders a piece of HTML.
                The Tag models are dynamically created when the project information is initialized in a <ExternalLink href="https://en.wikipedia.org/wiki/Multiton_pattern" type="wiki">Multiton</ExternalLink>-fashion.
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Demo: Tag Component</>)}>
                <h3>Result</h3>
                <div className="code-demo">
                    <Tag model={getProjects().getTag("react-js")} showCount={true} />
                </div>
                <h3>React Component</h3>
                <p>
                    This Component is simple and only requires a <code>render</code> function.
                    All data is dynamically inserted from the <code>model</code> property.
                    If the <code>showCount</code> property is set, the Component also renders a number, signifying the number of occurrences of the Tag.
                </p>
                <Code language="javascript" languageIndicator="JSX" content={demo1_1} />  
                <div className="row">
                    <div className="col-sm-6">
                        <h3>React JSX code</h3>
                        <Code language="javascript" languageIndicator="JSX" content={demo1_2} />
                        <p>
                            The properties are passed as attributes of <code>Tag</code>.
                            In this example, I just hard-coded the <code>tagModel</code>.
                        </p>
                    </div>
                    <div className="col-sm-6">
                        <h3>Resulting HTML</h3>
                        <Code language="xml" languageIndicator="HTML" content={demo1_3} />
                        <p>
                            The HTML looks similar to the Component's JSX code but was dynamically assembled.
                        </p>
                    </div>
                </div>
            </CollapseBox>
            
            <p>
                The <code>TagList</code> Component below takes a collection of models and then renders multiple <code>Tags</code>.
                Nesting Components in this way makes complex interfaces both manageable and clean.
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Demo: TagList Component</>)}>
                <h3>Result</h3>
                <div className="code-demo">
                    <TagList
                        models={getProjects().getAllTags().filter((tag) => {
                            return tag.category === "Languages"
                        })}
                        showCount={true}
                    />
                </div>
                <h3>React Component</h3>
                <p>
                    Rendering multiple instances of another Component can be done inline using the <code>Array.map</code> function.
                    Notice the <code>key</code> attribute of the <code>li</code> tag that enables React to differentiate children Components dynamically during re-renders.
                    If the <code>key</code> has not changed, the Component must not be re-created or even re-rendered!
                </p>
                <Code language="javascript" languageIndicator="JSX" content={demo2_1} />
                <div className="row">
                    <div className="col-sm-6">
                        <h3>React JSX code</h3>
                        <Code language="javascript" languageIndicator="JSX" content={demo2_2} />
                        <p>
                            The list of Tag models can also be filtered using <code>Array.map</code>.
                            In this case, only the models that have their category set to <code>"Languages"</code> are passed to <code>TagList</code>
                        </p>
                    </div>
                    <div className="col-sm-6">
                        <h3>Resulting HTML</h3>
                        <Code language="xml" languageIndicator="HTML" content={demo2_3} />
                        <p>
                            The generated HTML is already big enough that I don't want to paste it all here.
                        </p>
                    </div>
                </div>
            </CollapseBox>
            <p>
                As mentioned earlier, React re-renders Components that need updating.
                This is the case when the Component's state is changed using <code>setState</code> or it could also happen when a parent Component's state has changed.
                What's truly genius about React is that re-rendering doesn't mean throwing out the old HTML and replacing it with the new one.
                Instead, React compares the two JSX structures and dynamically updates only the pieces of HTML that need changing - could be just one HTML attribute, an additional HTML tag or some CSS style rules.
                Not only is the amount of browser DOM changes and redraws drastically reduced this way, but events and user changes are preserved as well.
            </p>
            <p>
                In this last React demo, I adjusted the <code>TagList</code> from the previous example and added two optional filtering conditions that can be passed to TagList and are then stored in its <code>state</code>.
                When changing the state, a Component re-render is automatically triggered, applying the new filter.
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Demo: TagList Component with State</>)}>
                <h3>Result</h3>
                <div className="code-demo">
                    <PageDemo />
                </div>
                <h3>Updated React Component</h3>
                <Code language="javascript" languageIndicator="JSX" content={demo3_1} />
            <h3>Usage</h3>
            <Code language="javascript" languageIndicator="JSX" content={demo3_2} />
            </CollapseBox>
            <p>
                What I like about frameworks like <Tag model={getProjects().getTag("react-js")} /> or <Tag model={getProjects().getTag("backbone-js")} /> is how they enforce a certain architecture.
                With a good framework, you sometimes get the feeling that all the logic has already been written and you only have to fill in your templates and models.
                Using React enabled me to allow for complex behavior in the smallest details.
                When all videos on the page use the same <code>&lt;Video&gt;</code> Component, why not give it a global loading effect?
                Doing the same without a framework would have been incredibly tedious, because I would have to find all instances of videos and adjust them all.
            </p>
        </section>
        <section>
            <h2>Precompiled CSS</h2>
            <p>
                <ExternalLink href="https://sass-lang.com/">Syntactically Awesome Stylesheets (SCSS or SASS)</ExternalLink> is a language extension to CSS that adds variables, functions, control structures, nested styles and many more features on top of the rigid CSS.
                It is converted to CSS during development or build time. That means that at the time you see this website, your browser only has the CSS and never got to see the SCSS.
            </p>
            <p>
                Designing with CSS can be a little tedious at times and everyone working on a bigger project knows that it becomes increasingly hard to keep CSS code clean and consistent.
                The variable feature almost single-handedly solves that problem.
                Just define all main colors, font families and spaces in one place and you can change hundreds of style attributes by changing one variable.
            </p>
            <p>
                Contrary to CSS, SCSS feels like a real programming language.
                Using data structures and loops, you can even dynamically create CSS classes!
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Demo: Dynamic Button classes</>)}>
                <h3>Result</h3>
                <div className="code-demo">
                    <div className="row">
                        <div className="col-sm-4">
                            <Button
                                color="primary"
                                onClick={() => {}}
                                preventPageTransition={true}
                                block={true}
                                icon={"fa-1"}
                            >Primary</Button>
                        </div>
                        <div className="col-sm-4">
                            <div className="clearfix--xs space-content" />
                            <Button
                                color="secondary"
                                onClick={() => {}}
                                preventPageTransition={true}
                                block={true}
                                icon={"fa-2"}
                            >Secondary</Button>
                        </div>
                        <div className="col-sm-4">
                            <div className="clearfix--xs space-content" />
                            <Button
                                color="disabled"
                                onClick={() => {}}
                                preventPageTransition={true}
                                block={true}
                                icon={"fa-ban"}
                            >Disabled</Button>
                        </div>
                    </div>
                </div>
                <h3>Color palette definition</h3>
                <p>
                    I'm using a dictionary to define the desired CSS classes along with all dynamic properties.
                    In this case, it contains a color palette for buttons.
                </p>
                <Code language="scss" languageIndicator="SCSS" content={demo4_1} />
                <h3>Class generation</h3>
                <p>
                    The <code>@each</code> keyword iterates over the dictionary, creating a class <code>.bttn--#&#123;$name&#125;</code> with each pass.
                    Inside you will find the CSS rules that are filled in using the dictionary's color values.
                </p>
                <Code language="scss" languageIndicator="SCSS" content={demo4_2} />
            </CollapseBox>
            <p>
                When creating a React Component like a <code>Box</code> with title and image, a <code>Button</code> or a <code>ProjectView</code>, I often found myself creating a corresponding SCSS component of sorts.
                This way, optional styles for React Components passed as properties can just be implemented as additional CSS classes.
                The SCSS <code>@extend</code> keyword even allows extending other CSS selector rules.
                This can be useful to create helper classes that you can extend multiple times and it often suits the object oriented style of a React Component class hierarchy.
            </p>
        </section>
        <section>
            <h2>CI / CD</h2>
            <p>
                Like most React Apps, this website uses <ExternalLink href="https://www.nodejs.org/">Node.js</ExternalLink> as a runtime environment, <ExternalLink href="https://www.npmjs.com/">npm</ExternalLink> for package management and <ExternalLink href="https://webpack.js.org/">webpack</ExternalLink> for module packing.
                <br />
                Using the Node.js runtime environment, I can start a local development server using <code>$npm start</code> and the final result is built with <code>$npm run build</code>.
                The development server has many fancy features like hot-swapping when I change source files or source-mapping to facilitate debugging.
                The runtime environment is also responsible for precompiling the SCSS to CSS (at least after installing the SCSS package with npm).
                <br />
                Using webpack means I can split my JavaScript and SCSS modules into as many files and include as many dependencies as I like and then webpack bundles them all together into one minified JS and CSS file, making sure all asset paths are still correct.
            </p>
            <p>
                When building the result, all scripts, stylesheets and assets are bundled, optimized and minified - ready to be uploaded onto a server!
                When a project grows to a certain size, this process can take a minute or two.
                To actually deploy the website I then have to upload the build via FTP to the server, which takes around 5 minutes for this project.
            </p>
            <p>
                That process is already tedious enough, because I have to build, wait, click around an FTP application with poor interface design and then wait again.
                Even worse: At some point during development, I added a Python script that automatically generates image thumbnails.
                It also extracts the first frame of any video for video thumbnails and it is quite possible I am going to need more of these tools in the future.
                <br />
                With that many steps and that much waiting time involved, I decided I wanted some automation. 
            </p>
            <p>
                <SpecificTag name="continuous-integration" overwriteTitle="Continuous Integration" showCount={true} /> or Continuous Deployment is a brilliant concept that outsources testing, building and deployment to a server - in my case GitHub.
                GitHub Actions is a tool that automatically calls a sequence of commands whenever a certain condition is triggered, e.g. when someone pushes to a specific branch.
                These commands can be configured in a <ExternalLink href="https://en.wikipedia.org/wiki/YAML" type="wiki">YAML</ExternalLink> file.
                You can have a look at this website's CI workflow here:
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Github CI workflow</>)}>
                <p>
                    Each task is given a <code>name</code> and can either <code>run</code> a terminal command or <code>use</code> a pre-defined action or even an action defined in another repository.
                    There are quite a few comments in the code, so feel free to have a look at the complete task list:
                </p>
                <Code language="yaml" languageIndicator="YAML" content={demo_ci} />
            </CollapseBox>
            <p>
                When everything is set up and done, all you have to do is push and then watch the tasks being performed like magic.
                I find this small luxury especially important if you plan to make small changes frequently and/or you don't have a backend to add new content to.
                For this website specifically, I wanted a comfortable way to add a new project, screenshot or paragraph without having to block my creative flow with a repetitive and time-consuming task.
            </p>
            <ImgGallery images={[
                {
                    src : require("../../../media/projects/this-website/github-workflow-1920.png"),
                    src_thumb : require("../../../media/projects/this-website/github-workflow-1080.png"),
                    src_thumb_preload : require("../../../media/projects/this-website/github-workflow-32.png"),
                    alt : "Github Actions were used to achieve Continuous Integration / Continuous Deployment",
                    title : "CI/CD not only drastically improve the workflow but is also satisfying to watch!"
                }
                ]} />
        </section>
        <section>
            <h2>Shading the Header</h2>
            <p>
                Unless you've been visiting this website on mobile, you probably noticed the over-the-top hover and page transition effect in the header's navigation.
                If you're well-versed in CSS you might be wondering how to do it, but in fact, it's no CSS at all.
                In that navigation bar is a <code>&lt;canvas&gt;</code> tag that uses a shader to draw the effect.
            </p>
            <p>
                To get the shader running I added the <ExternalLink href="https://threejs.org/">Three.js</ExternalLink> library that I had already used in a <Link to="/project/quizzle">previous project</Link> way back in 2014.
                Three.js makes use of the WebGL API and supplies many 3D rendering tools.
                The only thing I use it for however, is creating a 2D plane and texturizing it with a shader.
            </p>
            <p>
                For the shader to work, it needs to know the positions and extents of the navigation links as well as the cursor position.
                Additionally, there needs to be a way to signify that a page was just loaded.
                All this information is passed to the shader via uniforms and must be updated when the user moves their cursor or when the window size changes.
            </p>
            <p>
                The visual effect is achieved by rendering a sine wave from the edges towards the mouse cursor's position in a rectangular shape over time.
                Additionally, the edges of the moving rectangles are distorted using another sine wave!
                When a navigation link is clicked or the page changes, it's just a matter of temporarily adjusting some parameters.
                The shader code itself is a little messy and hard to understand, but you can have a look at it nevertheless:
            </p>
            <CollapseBox title={(<><i className="h--i fa fa-file-code" />Shader</>)}>
                <h3>Vertex shader</h3>
                <Code language="glsl" languageIndicator="GLSL" content={demo_vertexShader} />
                <h3>Fragment shader</h3>
                <Code language="glsl" languageIndicator="GLSL" content={demo_fragmentShader} />
            </CollapseBox>
        </section>
    </>);
}

const demo_vertexShader = `void main() {
    // transform position according to object location and camera
    // there is a lot to explain here, but it's not really relevant for this example
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position.xyz, 1.0);
}`
const demo_fragmentShader = `uniform vec4[4] uiRects;
uniform vec2 mousePos;
uniform vec2 iResolution;
uniform float iTime;
uniform float clickEffect;
uniform int clickEffectUiRect;

const vec4 COL_BG = vec4(122.0/255.0, 49.0/255.0, 190.0/255.0, 1.0);
const vec3 COL_EFFECT = vec3(33.0/255.0, 176.0/255.0, 189.0/255.0);
const float RADIUS = 70.0;

// maps value from range [valueMin, valueMax] to [resultMin, resultMax] and clamps it to [resultMin, resultMax]
float rescale(float value, float valueMin, float valueMax, float resultMin, float resultMax) {
    return min(1.0, max(0.0, (value - valueMin) / (valueMax - valueMin))) * (resultMax - resultMin) + resultMin;
}
// ...the same without clamping
float rescaleUnclamped(float value, float valueMin, float valueMax, float resultMin, float resultMax) {
    return (value - valueMin) / (valueMax - valueMin) * (resultMax - resultMin) + resultMin;
}

// distance between point uv and rectangle
float dstAxisAlignedRect(vec2 uv, vec2 tl, vec2 br) {
    vec2 d = max(tl-uv, uv-br);
    return length(max(vec2(0.0), d)) + min(0.0, max(d.x, d.y));
}

void main() {
  vec2 pt = gl_FragCoord.xy;

  // always zero except for one second after uniform clickEffect was set to the current time
  // energyClickGlobal will be an inverted parabola between 0 and 1.
  float energyClickGlobal = rescale(iTime - clickEffect, 0.0, 1.0, -1.0, 1.0);
  energyClickGlobal = max(0.0, min(1.0, 1.0 - energyClickGlobal * energyClickGlobal));

  // Iterate the uiRects (navigation links) and collect energy
  float energy = 0.0;
  for (int i = 0; i < uiRects.length(); i++) {
    vec4 uiRect = uiRects[i];

    // only play click effect when this is the right uiRects
    float energyClick = (i == clickEffectUiRect) ? energyClickGlobal : 0.0;

    // calculate distances
    float dstPt = dstAxisAlignedRect(pt, uiRect.xy, uiRect.xy + uiRect.zw);
    float dstMouse = dstAxisAlignedRect(mousePos, uiRect.xy, uiRect.xy + uiRect.zw);

    // offset the uiRects center depending on the mouse position and calculate the distance to the uiRect
    vec2 center = uiRect.xy + 0.5 * uiRect.zw;
    vec2 centerOffset = -0.5 * (mousePos - center);
    centerOffset.x = min(uiRect.z * 0.5, max(-uiRect.z * 0.5, centerOffset.x));
    centerOffset.y = min(uiRect.w * 0.5, max(-uiRect.w * 0.5, centerOffset.y));
    float dstPtShifted = dstAxisAlignedRect(pt + centerOffset * (1.0 - energyClick), uiRect.xy, uiRect.xy + uiRect.zw);

    // check if this pixel is inside this uiRects
    float isInsideEnergy = rescale(dstPt, -2.0, 0.0, 1.0, 0.0);

    // check if this pixel is close to this uiRects edge
    float edgeEnergy = rescale(abs(dstPt), 2.0 + energyClick * 2.0, 0.0, 0.0, 1.0);

    // check if the mouse is within this uiRect
    float energyMouse = max(rescale(dstMouse, -10.0, 20.0, 1.0, 0.0), energyClick);

    // amplitude of the sine effect
    float amplWarp = 0.3 + energyClick * 0.7;

    // calculate waves of rectangles moving inwards
    float dstPtWarped = dstPtShifted + amplWarp * sin(5.0 * iTime + 0.4 * pt.x) + amplWarp * cos(5.0 * iTime + 0.4 * pt.y);
    float waveX = rescaleUnclamped(dstPtWarped, 0.0, -25.0, 0.0, 2.0 * 3.1415) - (2.5*energyClick);

    // calculate waves on the rectangles edges
    float waveY = sin(waveX * 3.0 - 22.0 * iTime) * 0.5 + 0.5;

    // remove opacity towards center
    float innerDecayEnergy = rescale(dstPtShifted, 0.0, -20.0, 1.0, 0.0);

    // multiply and add energies
    energy += (edgeEnergy + 0.7*(waveY * waveY * waveY + 0.6 * energyClick) * innerDecayEnergy * isInsideEnergy) * energyMouse;
  }

  // Draw background color an then add effect on top
  gl_FragColor.rgba = COL_BG;
  gl_FragColor.rgb += COL_EFFECT * energy;
  
}`

const demo1_1 = `import React, { Component } from 'react';
import Link from '../component/Link';

export default class Tag extends Component {
    render() {
        return (
            <Link
                className="tag"
                to={ this.props.model.href }
                style={{ backgroundColor : this.props.model.color }}
            >
                <span
                    className="tag__hover"
                    style={{ backgroundColor: this.props.model.hoverColor }}
                />
                <span className="tag__content">
                    { this.props.model.name }
                    { this.props.showCount && // only render if showCount property set
                        <span className="tag__count">
                            { this.props.model.count }
                        </span>
                    }
                </span>
            </Link>
        );
    }
}`;

const demo1_2 = `const tagModel = {
    name: "React.js",
    color: "rgb(138, 113, 15)",
    colorHover: "rgb(194, 157, 10)",
    href: "/projects/react-js",
    count: 1
}
return <Tag
    showCount={true}
    model={tagModel}
/>`;

const demo1_3 = `<a href="/projects/react" class="tag" style="background-color: rgb(138, 113, 15);">
    <i class="tag__hover" style="background-color: rgb(194, 157, 10);"></i>
    <span class="tag__content">
        React
        <span class="tag__count">1</span>
    </span>
</a>`;

const demo2_1 = `import React, { Component } from 'react';
import Tag from "./Tag";

export default class TagList extends Component {
    render() {
        return (<> 
            <ul className="ul--tag-list">
                {this.props.models.map((model, i) => (
                    <li key={model.name}>
                        <Tag model={model} showCount={this.props.showCount} />
                    </li>
                ))}
            </ul>
        </>);
    }
}`

const demo2_2 = `
return <TagList
    showCount={true}
    models={projects.getAllTags().filter(
        (tag) => tag.category === "Languages"
    })}
/>`;

const demo2_3 = `
<ul class="ul--tag-list">
    <li>
        <a href="/projects/javascript" class="tag" style="background-color: rgb(138, 113, 15);">
            <i class="tag__hover" style="background-color: rgb(194, 157, 10);"></i>
            <span class="tag__content">
                JavaScript
                <span class="tag__count">1</span>
            </span>
        </a>
    </li>
    <li>
        // ...
    </li>
    // ...
</ul>`;

const demo3_1 = `import React, { Component } from 'react';
import Tag from "./Tag";

class TagList extends Component {
    constructor(props) {
        super(props);
        // initializing state like this does not trigger a re-render
        this.state = {
            filterCategory: this.props.filterCategory || null,
            filterMinCount: this.props.filterMinCount || null
        };
    }
    setFilterCategory(filterCategory) {
        // updating the state triggers re-render
        this.setState({filterCategory : filterCategory});
    }
    setFilterMinCount(filterMinCount) {
        // updating the state triggers re-render
        this.setState({filterMinCount : filterMinCount});
    }
    render() {
        // shallow copy and then apply filters
        let models = [...this.props.models];
        if (this.state.filterCategory) {
            models = models.filter((model) => model.category === this.state.filterCategory)
        }
        if (this.state.filterMinCount) {
            models = models.filter((model) => model.count >= this.state.filterMinCount)
        }

        return (<> 
            <ul className="ul--tag-list">
                {models
                    .map((model, i) => (
                        <li key={model.name}>
                            <Tag model={model} showCount={this.props.showCount} />
                        </li>
                ))}
            </ul>
        </>);
    }
}`

const demo3_2 = `import React, { Component } from 'react';
import Button from "./Button";
import TagList from "./TagList";
import projects from "./projects";

class Page extends Component {
    constructor(props) {
        super(props);
        this.refTagList = React.createRef();
    }
    render() {
        return (<>
            <!-- Create TagList -->
            <TagListDemo
                models={getProjects().getAllTags()}
                filterCategory={"Categories"}
                filterMinCount={1}
                showCount={true}
                ref={this.refTagList}
            />
            <!-- Create category filter buttons -->
            <div className="row">
                {["Categories", "Languages", "Technologies"].map((category) => (
                    <div className="col-sm-4" key={category}>
                        <Button
                            onClick={() => this.refTagList.current.setFilterCategory(category)}
                            preventPageTransition={true}
                            block={true}
                        >{category}</Button>
                    </div>
                ))}
            </div>
            <!-- Create minimium count filter buttons -->
            <div className="row">
                {[1, 3, 5].map((count) => (
                    <div className="col-sm-4" key={count}>
                        <Button
                            onClick={() => this.refTagList.current.setFilterMinCount(count)}
                            preventPageTransition={true}
                            block={true}
                        >Minimium count: {count}</Button>
                    </div>
                ))}
            </div>
        </>);
    }
}`;

const demo4_1 = `$bttn-colors: (
    primary : (
        bg:             darken($brand-primary, 0%),
        bg-active:      darken($brand-secondary, 0%),
        icon-bg:        darken($brand-primary, 6%),
        icon-bg-active: darken($brand-secondary, 6%),
        color:          #fff,
        color-active:   #fff
    ),
    secondary : (
        bg:             darken($brand-secondary, 0%),
        bg-active:      darken($brand-secondary, 6%),
        icon-bg:        darken($brand-secondary, 4%),
        icon-bg-active: darken($brand-secondary, 10%),
        color:          #fff,
        color-active:   #fff
    ),
    disabled : (
        bg:             darken(#666, 0%),
        bg-active:      darken(#666, 0%),
        icon-bg:        darken(#666, 6%),
        icon-bg-active: darken(#666, 6%),
        color:          #fff,
        color-active:   #fff
    ),
);`;

const demo4_2 = `// Iterate button color themes
@each $name, colors in $bttn-colors {
    // dynamically create classes bttn--secondary, bttn--disabled, ...
    .bttn--#{$name} {

        // text and icon color
        &, &:link, &:visited, &:focus, &:active {
            color: map-get($colors, "color");
        }

        // background colors
        & .bttn__i {
            background-color: map-get($colors, "icon-bg");
        }
        & .bttn__content {
            background-color: map-get($colors, "bg");
        }

        // when button is hoverd or tagged as active
        &:hover, &.bttn--active {
            color: map-get($colors, "color-active");

            & .bttn__i {
                background-color: map-get($colors, "icon-bg-active");
            }
            & .bttn__content {
                background-color: map-get($colors, "bg-active");
            }
        }
    }
}`;

const demo_ci = `name: Build and Deploy

# triggers for the workflow to start
# here: push to master or pull request to master accepted
on:
  push:
    branches: [ "master" ]
  pull_request:
    branches: [ "master" ]

jobs:
  build:
    # OS to use
    runs-on: ubuntu-latest

    # Node.js runtime version
    # Multiple versions can be used for testing
    strategy:
      matrix:
        node-version: [18.x]

    # List of tasks to perform
    steps:

        # Github action to checkout repo into current directory
        - name: Checkout Repo
          uses: actions/checkout@v3

        # Setup Node.js of specified versions (here: only 18.x)
        - name: Setup Node.js ${"{ matrix.node-version }"}
          uses: actions/setup-node@v3
          with:
            node-version: ${"{ matrix.node-version }"}
            cache: 'npm'

        # Install Python dependencies for next step
        - name: Install Python dependencies
          run: pip3 install opencv-python pillow

        # Python script that creates thumbnails
        - name: Call imgResize.py to create thumbnails
          run: python3 imgResize.py

        # Install dependencies defined in package.json
        - name: Install npm dependencies
          run: npm install

        # Now finally build the project
        - name: Build npm
          run: CI=false && npm run build

        # Just some debug info and documentation
        - name: Show build directory
          run: ls -la build

        # Use action defined in another repository
        # Uploads build directory to specified FTP server
        # Always use GitHub Secrets and don't put login data online 
        - name: Sync files
          uses: SamKirkland/FTP-Deploy-Action@v2.0.0
          env:
            FTP_SERVER: ${"{{ secrets.FTP_SERVER }}"}
            FTP_USERNAME: ${"{{ secrets.FTP_USERNAME }}"}
            FTP_PASSWORD: ${"{{ secrets.FTP_PASSWORD }}"}
            PORT: 22
            METHOD: sftp
            LOCAL_DIR: build
            ARGS: --delete --exclude=logs/*`;


class TagListDemo extends Component {
    constructor(props) {
        super(props);
        // initializing state like this does not trigger a re-render
        this.state = {
            filterCategory: this.props.filterCategory || null,
            filterMinCount: this.props.filterMinCount || null
        };
    }
    setFilterCategory(filterCategory) {
        // updating the state triggers re-render
        this.setState({filterCategory : filterCategory});
    }
    setFilterMinCount(filterMinCount) {
        // updating the state triggers re-render
        this.setState({filterMinCount : filterMinCount});
    }
    render() {
        // shallow copy and then apply filters
        let models = [...this.props.models];
        if (this.state.filterCategory) {
            models = models.filter((model) => model.category === this.state.filterCategory)
        }
        if (this.state.filterMinCount) {
            models = models.filter((model) => model.count >= this.state.filterMinCount)
        }

        return (<> 
            <ul className="ul--tag-list">
                {models
                    .map((model, i) => (
                        <li key={model.title}>
                            <Tag model={model} showCount={this.props.showCount} />
                        </li>
                ))}
            </ul>
        </>);
    }
}

class PageDemo extends Component {
    constructor(props) {
        super(props);
        this.refTagList = React.createRef();
    }
    render() {
        return (<>
            <div className="space-content">
                <TagListDemo
                    models={getProjects().getAllTags()}
                    filterCategory={"Categories"}
                    filterMinCount={1}
                    showCount={true}
                    ref={this.refTagList}
                />
            </div>
            <div className="row space-content">
                {["Categories", "Languages", "Technologies"].map((category, i) => (
                    <div className="col-sm-4" key={category}>
                        {i > 0 &&
                            <div className="clearfix--xs space-content" />
                        }
                        <Button
                            onClick={() => this.refTagList.current.setFilterCategory(category)}
                            preventPageTransition={true}
                            block={true}
                        >{category}</Button>
                    </div>
                ))}
            </div>
            <div className="row">
                {[1, 3, 5].map((count, i) => (
                    <div className="col-sm-4" key={count}>
                        {i > 0 &&
                            <div className="clearfix--xs space-content" />
                        }
                        <Button
                            onClick={() => this.refTagList.current.setFilterMinCount(count)}
                            preventPageTransition={true}
                            block={true}
                        >Minimium count: {count}</Button>
                    </div>
                ))}
            </div>
        </>);
    }
}